import { axiosObject, baseURL, HttpMethods, makeRequest } from '../helpers/http.helper';
import { isDemoMode } from '../demo/core';
import type {
  CollectionPointPagination,
  CollectionPointQuery,
  ImportResult,
  SupplierCollectionPoint,
  SupplierCollectionPointInput,
  SupplierCollectionPointUpdateInput,
} from '../types/supplier.type';

interface GenericResponse<T> {
  result: T;
}

export async function listCollectionPoints(params: CollectionPointQuery): Promise<CollectionPointPagination> {
  const response = await makeRequest({
    url: '/ops/collection-points',
    method: HttpMethods.GET,
    params,
  }) as GenericResponse<CollectionPointPagination>;
  return response.result;
}

export async function createCollectionPoint(
  organizationId: string,
  input: SupplierCollectionPointInput,
): Promise<SupplierCollectionPoint> {
  const response = await makeRequest({
    url: '/ops/collection-points',
    method: HttpMethods.POST,
    params: { organization_id: organizationId },
    data: input,
  }) as GenericResponse<SupplierCollectionPoint>;
  return response.result;
}

export async function updateCollectionPoint(
  organizationId: string,
  collectionPointId: string,
  input: SupplierCollectionPointUpdateInput,
): Promise<SupplierCollectionPoint> {
  const response = await makeRequest({
    url: `/ops/collection-points/${collectionPointId}`,
    method: HttpMethods.PATCH,
    params: { organization_id: organizationId },
    data: input,
  }) as GenericResponse<SupplierCollectionPoint>;
  return response.result;
}

export async function archiveCollectionPoint(organizationId: string, collectionPointId: string): Promise<void> {
  await makeRequest({
    url: `/ops/collection-points/${collectionPointId}`,
    method: HttpMethods.DELETE,
    params: { organization_id: organizationId },
  });
}

export async function downloadCollectionPointImportTemplate(organizationId: string): Promise<Blob> {
  if (isDemoMode()) return new Blob(['name,contact_name,contact_phone,city\n'], { type: 'text/csv' });
  const response = await axiosObject.get(`${baseURL}/ops/collection-points/import-template`, {
    params: { organization_id: organizationId },
    responseType: 'blob',
  });
  return response.data as Blob;
}

export async function importCollectionPoints(organizationId: string, file: File): Promise<ImportResult> {
  // Demo: imports are not processed; report an empty result.
  if (isDemoMode()) return { total_rows: 0, created: 0, failed: [] };
  const formData = new FormData();
  formData.append('file', file);
  const response = await axiosObject.post(`${baseURL}/ops/collection-points/import`, formData, {
    params: { organization_id: organizationId },
  });
  return (response.data as GenericResponse<ImportResult>).result;
}

export async function parseGoogleMapsLink(
  organizationId: string,
  url: string,
): Promise<{ latitude: number; longitude: number }> {
  const response = await makeRequest({
    url: '/ops/collection-points/parse-maps-link',
    method: HttpMethods.POST,
    params: { organization_id: organizationId },
    data: { url },
  }) as GenericResponse<{ latitude: number; longitude: number }>;
  return response.result;
}
